import { getATLContextDomain } from '@atlaskit/atlassian-context/get-atl-context-domain';

import { DEFAULT_HEARTBEAT_ENDPOINT, DEFAULT_IDLE_TIME, INTERVAL_TIME } from '../../constants';
import BrowserInteractionTime from '../../utils/browser-interaction-time';
import { postHeartbeat } from './post';

// oxlint-disable-next-line eslint/no-redeclare
export type HeartbeatService = {
	isAuthenticated: () => boolean;
	isInitialized: () => boolean;
	init: () => void;
	cleanup: () => void;
};

const redirect = (): void => {
	const identity = getATLContextDomain('id');

	// Hardcoding to Jira as this will be the only adopter of this service for now.
	const redirectUrl = `https://${identity}/login?application=jira&continue=${encodeURIComponent(
		window.location.href,
	)}`;

	window.location.assign(redirectUrl);
};

const timeIsBeforeNow = (dateTime: Date | undefined): boolean => {
	return !!(dateTime && dateTime.getTime() <= Date.now());
};

const HeartbeatService = (): HeartbeatService => {
	let timer: BrowserInteractionTime | undefined;
	let heartBeatInterval: NodeJS.Timeout | undefined;
	let nextCallTimestamp: Date | undefined;
	let sessionExpiryTimestamp: Date | undefined;
	let initialized = false;
	let authenticated = false;
	let userActive = true;
	let tabActive = true;

	const isAuthenticated = (): boolean => {
		return authenticated;
	};

	const isInitialized = (): boolean => {
		return initialized;
	};

	const init = (): void => {
		// Initial heartbeat
		beat();
		heartBeatInterval = setupHeartbeatInterval();
		timer = setupBrowserInteractionTime();
	};

	const cleanup = (): void => {
		clearHeartbeatInterval();
		clearBrowserInteractionTime();
	};

	const beat = async (): Promise<void> => {
		try {
			const response = await postHeartbeat(DEFAULT_HEARTBEAT_ENDPOINT, redirect);
			if (!initialized) {
				initialized = true;
			}
			if (response) {
				const { nextCallTimestamp: nextCallT, sessionExpiryTime: sessionExpiryT } = response;
				nextCallTimestamp = new Date(nextCallT);
				sessionExpiryTimestamp = sessionExpiryT ? new Date(sessionExpiryT) : undefined;
				authenticated = true;
			}
		} catch (error) {
			// Silent degradation - heartbeat service should not interfere with application
			authenticated = false;
			clearHeartbeatInterval();
		}
	};

	const setupHeartbeatInterval = (): NodeJS.Timeout =>
		setInterval(() => {
			if (
				timeIsBeforeNow(sessionExpiryTimestamp) ||
				(userActive && tabActive && timeIsBeforeNow(nextCallTimestamp))
			) {
				beat();
			}
		}, INTERVAL_TIME);

	const clearHeartbeatInterval = (): void => {
		clearInterval(heartBeatInterval);
		heartBeatInterval = undefined;
	};

	const setupBrowserInteractionTime = (): BrowserInteractionTime => {
		const timer = new BrowserInteractionTime({
			idleTimeoutMs: DEFAULT_IDLE_TIME,
			stopTimerOnTabchange: false,
			browserTabActiveCallbacks: [() => (tabActive = true)],
			browserTabInactiveCallbacks: [() => (tabActive = false)],
			idleCallbacks: [
				() => {
					// Beat once when the user goes idle and the tab is active
					if (tabActive) {
						beat();
					}
					userActive = false;
				},
			],
			activeCallbacks: [() => (userActive = true)],
		});

		timer.startTimer();

		return timer;
	};

	const clearBrowserInteractionTime = (): void => {
		timer && timer.destroy();
		timer = undefined;
	};

	return {
		isAuthenticated,
		isInitialized,
		init,
		cleanup,
	};
};

let instance: HeartbeatService | null = null;

export const getInstance = (): HeartbeatService => {
	if (!instance) {
		instance = HeartbeatService();
		instance.init();
	}
	return instance;
};
